import { computed, ref, watch } from 'vue'
import { ISoundItem, ISoundItemType } from '@/types/sound'
import { useStorage } from '@vueuse/core'
import { TranslateResult } from 'vue-i18n'
import { timeFormatter } from '@/helpers/timeFormatter'
import { getInitialTime } from '@/util/timeUtils'

const SOUND_VOLUME_KEY = 'SoundVolumeState'
const DEFAULT_SOUND_VOLUME = 0.2

const soundsQueueState = ref<Array<ISoundItem>>([])
// const isPlaying = ref<boolean>(false)
let audioContext: AudioContext | null = null
const gainNode = ref<GainNode | null>(null)
const currentSource = ref<AudioBufferSourceNode | null>(null)

const soundsQueueHistory = ref<ISoundItem[]>([])

export const soundMutedState = ref(true)
export const soundVolumeState = useStorage(SOUND_VOLUME_KEY, DEFAULT_SOUND_VOLUME)

export const soundsLength = computed(() => {
    return soundsQueueState.value.length
})
export const isSoundMuted = computed(() => {
    return soundMutedState.value
})
export const soundVolumeStateValue = computed(() => {
    return +soundVolumeState.value
})

function comparer (sound1: ISoundItem, sound2: ISoundItem) {
    return sound1.id === sound2.id && sound1.type === sound2.type
}

function getQueueSoundItem (sound: ISoundItem) {
    return soundsQueueState.value.find(i => comparer(i, sound))
}

export function setupSoundQueue (sound: ISoundItem) {
    const exist = getQueueSoundItem(sound)
    if (!exist) {
        soundsQueueState.value.push(sound)
        setTimeout(playSounds, 100)
    }
}

export function updateSoundPlayedState (sound: ISoundItem, state = true) {
    if (!sound) {
        return
    }
    soundsQueueState.value = soundsQueueState.value.map(i => {
        if (comparer(i, sound)) {
            i.played = state
        }

        return i
    })
}

export function updateSoundPlayingState (sound: ISoundItem, state: boolean) {
    if (!sound) {
        return
    }
    soundsQueueState.value = soundsQueueState.value.map(i => {
        if (comparer(i, sound)) {
            i.isPlaying = state
        }

        return i
    })
}

export function removeSoundsFromQueueById (soundId: string) {
    soundsQueueState.value = [ ...soundsQueueState.value ].filter(i => i.id !== soundId)
}

const MAX_SOUND_HISTORY = 100

export function removeSoundsFromQueueByIdWithHistoryKeep (soundId: string) {
    const { remainingSounds, removedSounds } = soundsQueueState.value.reduce(
        (acc, sound) => {
            if (sound.id === soundId) {
                sound.played = true
                if (sound.timerStartValue) {
                    const finishedTime = getInitialTime(sound.timerStartValue)
                    sound.notificationHTML+= timeFormatter(finishedTime)
                    delete sound.timerStartValue
                }
                acc.removedSounds.push(sound)
            } else {
                acc.remainingSounds.push(sound)
            }
            return acc
        },
        {
            remainingSounds: [],
            removedSounds: []
        }
    )
    if (removedSounds.length) {
        soundsQueueHistory.value.push(...removedSounds)
        if (soundsQueueHistory.value.length > MAX_SOUND_HISTORY) {
            soundsQueueHistory.value = soundsQueueHistory.value.slice(-MAX_SOUND_HISTORY)
        }
    }

    soundsQueueState.value = remainingSounds
}

export function updateSoundCurrentValue (soundId: string, notificationHtml: string| TranslateResult) {
    if (!soundId) {
        return
    }
    const sound = soundsQueueState.value.find(sound => sound.id === soundId)
    if (sound) {
        sound.notificationHTML = notificationHtml
    }
}


export function removeSoundsFromQueueByIdAndType (soundId: string, type: ISoundItemType) {
    soundsQueueState.value = [ ...soundsQueueState.value ].filter(i => i.id !== soundId && i.type !== type)
}

export function clearSoundsQueue () {
    soundsQueueState.value = []
    if (currentSource.value) {
        currentSource.value?.stop()
        currentSource.value = null
    }
}

export function updateSoundMutedState (state: boolean) {
    soundMutedState.value = state
    if (state && !soundVolumeStateValue.value) {
        soundVolumeState.value = DEFAULT_SOUND_VOLUME
    }
}

export function getQueueSounds () {
    return [ ...soundsQueueState.value ].sort((a, b) => a.timestamp - b.timestamp)
}

export function getQueueSoundHistory () {
    return soundsQueueHistory.value
}

export function clearSoundsHistory () {
    soundsQueueHistory.value = []
}

export function updateTimerStateValue (soundId: string, currentValue: number) {
    const sound = soundsQueueState.value.find(sound => sound.id === soundId)
    if (sound) {
        sound.timerStartValue = currentValue
    }
}

export function getSoundsLength () {
    return soundsQueueState.value.length
}

function updateGain () {
    if (!audioContext) {
        return
    }
    if (!gainNode.value) {
        gainNode.value = audioContext.createGain()
    }
    gainNode.value.gain.value = soundMutedState.value ? 0 : soundVolumeStateValue.value
}

function playSounds () {
    if (!audioContext) {
        return
    }
    const sounds = getQueueSounds()
    const isPlaying = !!sounds.find((s) => s.isPlaying)
    if (!sounds.length || isPlaying) {
        return
    }
    const soundToPlay = sounds.find(sound => !sound.played)
    if (soundToPlay) {
        const soundUrl = soundToPlay.sound
        updateSoundPlayingState(soundToPlay, true)
        fetch(soundUrl)
            .then(response => response.arrayBuffer())
            .then(buffer => audioContext?.decodeAudioData(buffer))
            .then(audioBuffer => {
                if (audioContext && audioBuffer) {
                    currentSource.value = audioContext.createBufferSource()
                    currentSource.value.buffer = audioBuffer
                    gainNode.value = audioContext.createGain()
                    updateGain()
                    currentSource.value.connect(gainNode.value).connect(audioContext.destination)
                    currentSource.value.onended = () => {
                        updateSoundPlayedState(soundToPlay)
                        updateSoundPlayingState(soundToPlay, false)
                        if (getSoundsLength() > 0) {
                            playSounds()
                        }
                    }
                    currentSource.value.start(0)
                }
            })
            .catch((e) => {
                console.log('CATCH!!!', e)
                updateSoundPlayingState(soundToPlay, false)
            })
    }
}

watch(
    soundMutedState,
    () => {
        if (!audioContext) {
            audioContext = new (window.AudioContext || window.webkitAudioContext)()
        }
        updateGain()
        playSounds()
    }
)
watch(
    soundVolumeStateValue,
    updateGain
)
